import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Users, Plus, Trash2, Shield, User, KeyRound, Eye, EyeOff, Copy, Check, Ban, Star, Sparkles, Mail, Clock, CreditCard, TrendingUp, DollarSign } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { API } from '@/App';

const TIER_LABELS = {
  essential: { label: 'Essentiel', color: 'bg-slate-100 text-slate-700' },
  normal: { label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  premium: { label: 'Premium', color: 'bg-amber-100 text-amber-700' }
};

const SUBSCRIPTION_LABELS = {
  standard: { label: 'Standard', color: 'bg-blue-100 text-blue-700', price: 29.99 },
  premium: { label: 'Premium', color: 'bg-amber-100 text-amber-700', price: 99 },
  admin: { label: 'Admin', color: 'bg-purple-100 text-purple-700', price: 0 },
  demo: { label: 'Démo', color: 'bg-slate-100 text-slate-700', price: 0 }
};

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [stripeWebhook, setStripeWebhook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [userDetailOpen, setUserDetailOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [webhookCopied, setWebhookCopied] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company_name: '',
    role: 'user'
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    try {
      const res = await axios.get(`${API}/admin/check`);
      setIsAdmin(res.data.is_admin);
      if (res.data.is_admin) {
        fetchUsers();
        fetchSubscriptionStats();
        fetchStripeWebhook();
      }
    } catch (error) {
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    }
  };

  const fetchSubscriptionStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/subscription-stats`);
      setSubscriptionStats(response.data);
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
    }
  };

  const fetchStripeWebhook = async () => {
    try {
      const response = await axios.get(`${API}/stripe/webhook-url`);
      setStripeWebhook(response.data);
    } catch (error) {
      console.error('Error fetching Stripe webhook URL:', error);
    }
  };

  const copyWebhookUrl = () => {
    if (stripeWebhook?.webhook_url) {
      navigator.clipboard.writeText(stripeWebhook.webhook_url);
      setWebhookCopied(true);
      setTimeout(() => setWebhookCopied(false), 2000);
      toast.success('URL copiée');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/users`, formData);
      toast.success('Utilisateur créé');
      setFormData({ email: '', password: '', company_name: '', role: 'user' });
      setOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (e, userId, email) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer l'utilisateur ${email} et toutes ses données ?`)) return;
    
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Utilisateur supprimé');
      setUserDetailOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowPassword(false);
    setCopied(false);
    setUserDetailOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setResetting(true);
    try {
      const response = await axios.post(`${API}/admin/users/${selectedUser.user_id}/reset-password`);
      setNewPassword(response.data.new_password);
      setShowPassword(true);
      toast.success('Mot de passe réinitialisé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setResetting(false);
    }
  };

  const handleUpdateTier = async (tier) => {
    if (!selectedUser) return;
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}/tier?tier=${tier}`);
      toast.success(`Niveau mis à jour: ${TIER_LABELS[tier].label}`);
      setSelectedUser({ ...selectedUser, account_tier: tier });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleToggleSuspend = async () => {
    if (!selectedUser) return;
    const newStatus = !selectedUser.account_suspended;
    try {
      await axios.put(`${API}/admin/users/${selectedUser.user_id}/suspend?suspended=${newStatus}`);
      toast.success(newStatus ? 'Compte suspendu' : 'Compte réactivé');
      setSelectedUser({ ...selectedUser, account_suspended: newStatus });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleCleanupOldRequests = async () => {
    if (!window.confirm('Supprimer les demandes sans réponse depuis plus d\'un mois ?')) return;
    try {
      const response = await axios.post(`${API}/admin/cleanup-old-requests`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Erreur lors du nettoyage');
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copié dans le presse-papier');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Erreur lors de la copie');
    }
  };

  const handleProcessFollowUps = async () => {
    try {
      const response = await axios.post(`${API}/admin/process-follow-ups`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Erreur lors du traitement des relances');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="p-8 text-center">
          <Shield className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-900 mb-2">Accès refusé</h2>
          <p className="text-slate-500">Cette page est réservée aux administrateurs.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 overflow-x-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2 sm:gap-3">
            <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            Administration
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1 sm:mt-2">Gestion des utilisateurs et comptes</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Button onClick={handleProcessFollowUps} variant="outline" className="text-sm" data-testid="process-followups-btn">
            <Mail className="w-4 h-4 mr-1" /> Relances
          </Button>
          <Button onClick={handleCleanupOldRequests} variant="outline" className="text-sm" data-testid="cleanup-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Nettoyer
          </Button>
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-white">
                <Plus className="w-4 h-4 mr-2" /> Nouvel utilisateur
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Créer un utilisateur</DialogTitle>
                <DialogDescription>Ajoutez un nouvel utilisateur au système.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1"
                    placeholder="email@exemple.com"
                  />
                </div>
                <div>
                  <Label>Mot de passe</Label>
                  <Input
                    type="password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1"
                    placeholder="••••••••"
                  />
                </div>
                <div>
                  <Label>Nom de l'entreprise</Label>
                  <Input
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="mt-1"
                    placeholder="Ma Société"
                  />
                </div>
                <div>
                  <Label>Rôle</Label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    className="mt-1 w-full h-10 px-3 border border-slate-200 rounded-md"
                  >
                    <option value="user">Utilisateur</option>
                    <option value="admin">Administrateur</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                    Annuler
                  </Button>
                  <Button type="submit" disabled={submitting} className="flex-1 bg-primary text-white">
                    {submitting ? 'Création...' : 'Créer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4 bg-white border border-slate-200">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" />
            <div>
              <p className="text-2xl font-bold">{users.length}</p>
              <p className="text-sm text-slate-500">Total</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border border-slate-200">
          <div className="flex items-center gap-3">
            <Star className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-2xl font-bold">{users.filter(u => u.account_tier === 'premium').length}</p>
              <p className="text-sm text-slate-500">Premium</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border border-slate-200">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{users.filter(u => u.role === 'admin').length}</p>
              <p className="text-sm text-slate-500">Admins</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-white border border-slate-200">
          <div className="flex items-center gap-3">
            <Ban className="w-8 h-8 text-red-500" />
            <div>
              <p className="text-2xl font-bold">{users.filter(u => u.account_suspended).length}</p>
              <p className="text-sm text-slate-500">Suspendus</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Subscription Stats */}
      {subscriptionStats && (
        <Card className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-6 h-6 text-emerald-600" />
            <h2 className="text-lg font-bold text-slate-900">Statistiques Abonnements</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div className="bg-white rounded-lg p-4 border border-emerald-100">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-xs text-slate-500">Actifs</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{subscriptionStats.active_subscriptions}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-emerald-100">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-emerald-600" />
                <span className="text-xs text-slate-500">Revenu mensuel</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{subscriptionStats.monthly_revenue.toFixed(0)} €</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-emerald-100">
              <div className="flex items-center gap-2 mb-1">
                <Ban className="w-4 h-4 text-red-500" />
                <span className="text-xs text-slate-500">Bloqués</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{subscriptionStats.blocked_users}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-emerald-100">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="text-xs text-slate-500">Total utilisateurs</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{subscriptionStats.total_users}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-3 border border-blue-200 text-center">
              <p className="text-xs text-blue-600 mb-1">Standard</p>
              <p className="text-xl font-bold text-blue-700">{subscriptionStats.tier_counts?.standard || 0}</p>
              <p className="text-xs text-slate-400">{SUBSCRIPTION_LABELS.standard.price}€/mois</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-amber-200 text-center">
              <p className="text-xs text-amber-600 mb-1">Premium</p>
              <p className="text-xl font-bold text-amber-700">{subscriptionStats.tier_counts?.premium || 0}</p>
              <p className="text-xs text-slate-400">{SUBSCRIPTION_LABELS.premium.price}€/mois</p>
            </div>
          </div>
          
          {/* Trial users */}
          <div className="mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
            <p className="text-xs text-indigo-600 mb-1">En période d'essai (30 jours)</p>
            <p className="text-xl font-bold text-indigo-700">{subscriptionStats.trial_users || 0}</p>
          </div>
        </Card>
      )}

      {/* Stripe Webhook Configuration */}
      {stripeWebhook && (
        <Card className="p-6 bg-violet-50 border-violet-200">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-6 h-6 text-violet-600" />
            <h2 className="text-lg font-bold text-slate-900">Configuration Stripe Webhook</h2>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            URL dynamique pour la configuration du webhook Stripe.
          </p>
          <div className="bg-white rounded-lg p-3 border border-violet-200 mb-3">
            <Label className="text-xs text-slate-500 mb-1 block">Webhook URL</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-mono text-violet-700 bg-violet-50 px-2 py-1 rounded break-all">
                {stripeWebhook.webhook_url}
              </code>
              <Button variant="outline" size="sm" onClick={copyWebhookUrl} className="shrink-0">
                {webhookCopied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          {stripeWebhook.events_to_subscribe && (
            <div className="bg-white rounded-lg p-3 border border-slate-200">
              <Label className="text-xs text-slate-500 mb-2 block">Events</Label>
              <div className="flex flex-wrap gap-2">
                {stripeWebhook.events_to_subscribe.map((event, idx) => (
                  <span key={idx} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">{event}</span>
                ))}
              </div>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-3">{stripeWebhook.instructions}</p>
        </Card>
      )}

      {/* Users List */}
      <Card className="bg-white border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900">Liste des utilisateurs</h2>
          <p className="text-sm text-slate-500 mt-1">Cliquez sur un utilisateur pour gérer son compte</p>
        </div>
        <div className="divide-y divide-slate-100">
          {users.map((user) => (
            <div 
              key={user.user_id} 
              className={`p-3 sm:p-4 hover:bg-slate-50 cursor-pointer transition-colors ${user.account_suspended ? 'bg-red-50/50' : ''}`}
              onClick={() => handleUserClick(user)}
            >
              <div className="flex items-start sm:items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${user.account_suspended ? 'bg-red-100' : user.role === 'admin' ? 'bg-amber-100' : 'bg-slate-100'}`}>
                    {user.account_suspended ? (
                      <Ban className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                    ) : user.role === 'admin' ? (
                      <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                    ) : (
                      <User className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`font-semibold text-sm sm:text-base truncate ${user.account_suspended ? 'text-red-700' : 'text-slate-900'}`}>
                      {user.company_name}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-500 truncate">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                  <span className={`px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-full ${TIER_LABELS[user.account_tier || 'essential'].color}`}>
                    {TIER_LABELS[user.account_tier || 'essential'].label}
                  </span>
                  {user.role === 'admin' && (
                    <span className="px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-full bg-amber-100 text-amber-700">
                      Admin
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* User Detail Dialog */}
      <Dialog open={userDetailOpen} onOpenChange={setUserDetailOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedUser?.account_suspended ? (
                <Ban className="w-5 h-5 text-red-500" />
              ) : selectedUser?.role === 'admin' ? (
                <Shield className="w-5 h-5 text-amber-500" />
              ) : (
                <User className="w-5 h-5 text-primary" />
              )}
              Gestion du compte
            </DialogTitle>
            <DialogDescription>
              {selectedUser?.account_suspended ? 'Compte suspendu' : 'Informations et paramètres'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 mt-4">
              {/* User Info */}
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-xs text-slate-500">Entreprise</Label>
                  <p className="font-semibold text-slate-900">{selectedUser.company_name}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-sm text-slate-900 flex-1 break-all">{selectedUser.email}</p>
                    <Button variant="ghost" size="sm" onClick={() => copyToClipboard(selectedUser.email)} className="p-1 h-auto">
                      {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Account Tier */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Star className="w-4 h-4 text-slate-500" />
                  <Label className="font-semibold">Niveau du compte</Label>
                </div>
                <div className="flex gap-2">
                  {Object.entries(TIER_LABELS).map(([key, { label, color }]) => (
                    <Button
                      key={key}
                      variant={selectedUser.account_tier === key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleUpdateTier(key)}
                      className={selectedUser.account_tier === key ? 'bg-primary' : ''}
                    >
                      {label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Suspend Account */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Ban className="w-4 h-4 text-slate-500" />
                  <Label className="font-semibold">Statut du compte</Label>
                </div>
                <Button
                  onClick={handleToggleSuspend}
                  variant={selectedUser.account_suspended ? 'default' : 'outline'}
                  className={`w-full ${selectedUser.account_suspended ? 'bg-green-600 hover:bg-green-700' : 'text-red-600 border-red-200 hover:bg-red-50'}`}
                >
                  {selectedUser.account_suspended ? (
                    <>
                      <Check className="w-4 h-4 mr-2" /> Réactiver le compte
                    </>
                  ) : (
                    <>
                      <Ban className="w-4 h-4 mr-2" /> Suspendre le compte
                    </>
                  )}
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  {selectedUser.account_suspended 
                    ? 'L\'utilisateur ne peut plus accéder à son compte' 
                    : 'Suspendre empêche l\'accès sans supprimer les données'}
                </p>
              </div>

              {/* Password Reset */}
              <div className="border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <KeyRound className="w-4 h-4 text-slate-500" />
                  <Label className="font-semibold">Mot de passe</Label>
                </div>
                
                {newPassword ? (
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-xs text-green-700 mb-2">Nouveau mot de passe :</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 bg-white px-3 py-2 rounded border font-mono text-sm break-all">
                          {showPassword ? newPassword : '••••••••••••'}
                        </code>
                        <Button variant="ghost" size="sm" onClick={() => setShowPassword(!showPassword)} className="p-2 h-auto">
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(newPassword)} className="p-2 h-auto">
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Button onClick={handleResetPassword} disabled={resetting} className="w-full bg-primary text-white">
                    <KeyRound className="w-4 h-4 mr-2" />
                    {resetting ? 'Réinitialisation...' : 'Réinitialiser le mot de passe'}
                  </Button>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setUserDetailOpen(false)} className="flex-1">
                  Fermer
                </Button>
                <Button
                  variant="destructive"
                  onClick={(e) => handleDeleteUser(e, selectedUser.user_id, selectedUser.email)}
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
